<template>
  <!-- Kennzahl-Liste im Stil der Jira-Liste: Name links, Werte rechts, aufklappbare Unterzeilen (▶).
       Für viele gleichrangige Zahlen – schneller lesbar als eine Kachelwand. -->
  <div class="mb-kpitable-wrap">
    <table class="mb-kpitable">
      <thead>
        <tr>
          <th v-for="(column, index) in columns" :key="column.key" scope="col" :class="{ num: index > 0 && !column.text }">
            {{ column.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in visibleRows"
          :key="row.key"
          :class="[
            `mb-kpitable__row--level-${row.level || 0}`,
            { 'mb-kpitable__row--expandable': hasChildren(row), 'mb-kpitable__row--muted': row.muted }
          ]"
          @click="hasChildren(row) ? toggle(row.key) : null"
        >
          <td class="mb-kpitable__name">
            <span class="mb-kpitable__name-inner" :style="{ paddingLeft: `${(row.level || 0) * 16}px` }">
              <span
                class="mb-kpitable__toggle"
                :class="{ 'mb-kpitable__toggle--open': isOpen(row.key), 'mb-kpitable__toggle--placeholder': !hasChildren(row) }"
                aria-hidden="true"
              >▶</span>
              <span v-if="row.swatch" class="mb-swatch" :class="row.swatch" aria-hidden="true" />
              <span :title="row.title || undefined">{{ row.label }}</span>
              <InfoTooltip v-if="row.info" :title="row.label" :text="row.info" />
            </span>
          </td>
          <td
            v-for="column in columns.slice(1)"
            :key="column.key"
            :class="{ num: !column.text }"
          >
            <router-link v-if="column.link && row.to" :to="row.to" class="mb-kpitable__link" @click.stop>
              Details <span aria-hidden="true">→</span>
            </router-link>
            <template v-else-if="!column.link">{{ cell(row, column) }}</template>
          </td>
        </tr>
        <tr v-if="!rows.length">
          <td :colspan="columns.length" class="mb-kpitable__empty">Keine Kennzahlen verfügbar.</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { formatKpi } from '../composables/formatters';
import InfoTooltip from './InfoTooltip.vue';

const props = defineProps({
  // [{ key, label, text?: true (linksbündig), link?: true }] – erste Spalte = Name
  columns: { type: Array, required: true },
  // [{ key, label, level, parentKey, values: { [columnKey]: kpi-card | string }, to, swatch, muted, info, title }]
  rows: { type: Array, default: () => [] }
});

const toggled = ref({});

function hasChildren(row) {
  return props.rows.some((item) => item.parentKey === row.key);
}

// Unterzeilen sind standardmäßig offen, damit keine Kennzahl versteckt ist.
function isOpen(key) {
  return toggled.value[key] !== false;
}

function toggle(key) {
  toggled.value = { ...toggled.value, [key]: !isOpen(key) };
}

const byKey = computed(() => Object.fromEntries(props.rows.map((row) => [row.key, row])));

const visibleRows = computed(() => props.rows.filter((row) => {
  let parent = row.parentKey ? byKey.value[row.parentKey] : null;
  while (parent) {
    if (!isOpen(parent.key)) return false;
    parent = parent.parentKey ? byKey.value[parent.parentKey] : null;
  }
  return true;
}));

function cell(row, column) {
  const value = row.values?.[column.key];
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'object') return formatKpi(value);
  return value;
}
</script>
